import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { addVehicleBindQuery, addVehicleFeatureQuery } from '@amfam/policy/add-vehicle/data-access';
import { EquipmentModel, EquipmentOption } from '@amfam/policy/add-vehicle/ui';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';

@Component({
  selector: 'ds-vehicle-feature-equipment',
  templateUrl: './vehicle-feature-equipment.component.html',
  styleUrls: ['../add-vehicle-container/add-vehicle-container.component.scss'],
  standalone: false
})
export class VehicleFeatureEquipmentComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEvent = new EventEmitter<boolean>();
  @Output() errorEvent = new EventEmitter<{ hasError: boolean }>();
  public vehicleRiskDescription$ = this.store.select(vehicleQuery.selectNewRiskDescription);
  public vehicleFeatureDescriptionList$ = this.store.select(
    addVehicleFeatureQuery.selectNewVehiclesFeaturesDescriptionList
  );

  public equipmentList$ = this.buildEquipmentList();
  public equipmentChecklist$ = this.buildEquipmentCheckList();
  public userState$ = this.store.select(userQuery.selectUserStateCode);
  public isStateEligibleForAntiTheftFeature$ = this.store
    .select(userQuery.selectUserStateCode)
    .pipe(
      map(
        userState =>
          !!this.copyService
            .getCopy('policy.newVehicleFeaturesAndEquipmentAntiTheftEligibleStates')
            .find(state => state === userState)
      )
    );
  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  public featuresEquipmentForm: UntypedFormGroup;
  public featuresCategoryForm: UntypedFormGroup;

  private vehicleFeaturesCodesList$ = this.store.select(
    addVehicleFeatureQuery.selectNewVehiclesFeaturesCodesList
  );
  private stop$ = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private copyService: CopyService,
    private spinnerService: LoadingSpinnerService
  ) {}

  ngOnInit(): void {
    this.spinnerService.stop();
    this.buildForm();
    this.handleError();
  }

  ngAfterViewInit() {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'FeaturesEquipment' }
      })
    );
  }

  buildForm() {
    this.featuresEquipmentForm = this.formBuilder.group({});
    combineLatest(
      this.userState$,
      this.store.select(BrandSelectors.selectIsPartner),
      this.vehicleFeatureDescriptionList$,
      (userStateCode, isPartner, features) => ({
        userStateCode,
        isPartner,
        vehicleFeatures: features
      })
    )
      .pipe(take(1))
      .subscribe(state => {
        const isPartnerAntiTheftEligibleState =
          state.isPartner &&
          this.copyService
            .getCopy(
              'policy.newVehicleFeaturesAndEquipmentAntiTheftEligibleStatesForPartnersWithCategories'
            )
            .find(stateName => stateName === state.userStateCode);
        if (isPartnerAntiTheftEligibleState) {
          this.featuresCategoryForm = this.formBuilder.group({});
        }
      });
  }

  handleCheckBoxInteraction(updatedItem: EquipmentOption) {
    if (
      updatedItem.optionKey === 'antiLockBreaks' ||
      updatedItem.optionKey === 'dayTimeRunningLight' ||
      updatedItem.optionKey === 'electronicStabilityControl'
    ) {
      this.featuresEquipmentForm.controls['needAgent'].setValue('');
    }
    if (updatedItem.optionKey === 'needAgent') {
      if (this.featuresEquipmentForm.controls['antiLockBreaks']) {
        this.featuresEquipmentForm.controls['antiLockBreaks'].setValue('');
      }

      if (this.featuresEquipmentForm.controls['dayTimeRunningLight']) {
        this.featuresEquipmentForm.controls['dayTimeRunningLight'].setValue('');
      }

      if (this.featuresEquipmentForm.controls['electronicStabilityControl']) {
        this.featuresEquipmentForm.controls['electronicStabilityControl'].setValue('');
      }
    }
  }

  handleCheckBoxCategoryInteraction(updatedItem) {
    if (['category1', 'category2', 'category3', 'category4'].indexOf(updatedItem.id) > -1) {
      this.featuresCategoryForm.controls['needAgent'].setValue('');
    }
    if (updatedItem.id === 'needAgent') {
      if (this.featuresCategoryForm.controls['category1']) {
        this.featuresCategoryForm.controls['category1'].setValue('');
      }

      if (this.featuresCategoryForm.controls['category2']) {
        this.featuresCategoryForm.controls['category2'].setValue('');
      }

      if (this.featuresCategoryForm.controls['category3']) {
        this.featuresCategoryForm.controls['category3'].setValue('');
      }

      if (this.featuresCategoryForm.controls['category4']) {
        this.featuresCategoryForm.controls['category4'].setValue('');
      }
    }
  }

  submitVehicleFeaturesInfo() {
    this.spinnerService.start({
      blockActions: true
    });

    this.vehicleFeaturesCodesList$.pipe(take(1)).subscribe(codesList => {
      let categoryPayload = {};

      if (
        this.featuresCategoryForm &&
        !_get(this.featuresCategoryForm, "controls['needAgent']").value
      ) {
        categoryPayload = {
          antiTheftCategoryOne: _get(this.featuresCategoryForm, "controls['category1']").value
            ? true
            : false,
          antiTheftCategoryTwo: _get(this.featuresCategoryForm, "controls['category2']").value
            ? true
            : false,
          antiTheftCategoryThree: _get(this.featuresCategoryForm, "controls['category3']").value
            ? true
            : false,
          antiTheftCategoryFour: _get(this.featuresCategoryForm, "controls['category4']").value
            ? true
            : false
        };
      }

      this.store.dispatch(
        VehicleInfoActions.setVehicleFeaturesInfo({
          featureInfo: {
            ...categoryPayload,
            antiLockBreaks: _get(this.featuresEquipmentForm, "controls['antiLockBreaks']")
              ? !!_get(this.featuresEquipmentForm, "controls['antiLockBreaks'].value")
              : true,

            dayTimeRunningLight: _get(this.featuresEquipmentForm, "controls['dayTimeRunningLight']")
              ? !!_get(this.featuresEquipmentForm, "controls['dayTimeRunningLight'].value")
              : true,

            airBagsIndicator: _get(this.featuresEquipmentForm, "controls['airBagsIndicator']")
              ? _get(this.featuresEquipmentForm, "controls['airBagsIndicator'].value")
              : this.airBagsIndicatorMap(codesList.find(code => code.indexOf('fa_dr') !== -1)),

            antiTheftDeviceIndicator: _get(
              this.featuresEquipmentForm,
              "controls['antiTheftDeviceIndicator']"
            )
              ? _get(
                  this.featuresEquipmentForm,
                  "controls['antiTheftDeviceIndicator'].value"
                ).replace('*', '')
              : _get(this.featuresCategoryForm, "controls['needAgent']")
                ? undefined
                : this.antiTheftDeviceIndicatorMap(
                    codesList.find(code => code.indexOf('aud_alm') !== -1)
                  ),

            electronicStabilityControl: _get(
              this.featuresEquipmentForm,
              "controls['electronicStabilityControl']"
            )
              ? !!_get(this.featuresEquipmentForm, "controls['electronicStabilityControl'].value")
              : true,

            needAgent: _get(this.featuresEquipmentForm, "controls['antiTheftDeviceIndicator']")
              ? !!_get(this.featuresEquipmentForm, "controls['antiTheftDeviceIndicator'].value")
              : false,

            kitCarOrReplica: false
          }
        })
      );
    });

    this.store.dispatch(
      fromRouterActions.Go({
        path: ['policies/add-vehicle/underwriting-questions']
      })
    );
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }
  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  private airBagsIndicatorMap(code: string) {
    switch (code) {
      case 'fa_dr_fa_fp':
        return 'Both';
      case 'fa_dr':
        return 'front';
      case 'fa_fp':
        return 'other';
      default:
        return 'none';
    }
  }

  private antiTheftDeviceIndicatorMap(code: string) {
    switch (code) {
      case 'aud_alm_pas':
        return 'passive';
      case 'aud_alm_act':
        return 'active';
      case 'aud_alm_act_pas':
        return 'other';
      default:
        return 'none';
    }
  }

  private buildEquipmentList(): Observable<EquipmentModel[]> {
    return this.vehicleFeaturesCodesList$.pipe(
      take(1),
      withLatestFrom(
        this.store.select(userQuery.selectUserStateCode),
        this.store.select(BrandSelectors.selectIsPartner)
      ),
      map(([codesList, userStateCode, isPartner]) => {
        const [
          equipmentOption1,
          equipmentOption2,
          equipmentOption3,
          equipmentOption4,
          equipmentOption5
        ] = this.copyService.getCopy('policy.newVehicleFeatureListEquipmentOptions');
        const returnArr: EquipmentModel[] = [];

        const isEligibleForAntiTheft = !!this.copyService
          .getCopy(
            isPartner
              ? 'policy.newVehicleFeaturesAndEquipmentAntiTheftEligibleStatesForPartners'
              : 'policy.newVehicleFeaturesAndEquipmentAntiTheftEligibleStates'
          )
          .find(state => state === userStateCode);

        if (
          !codesList.find(
            code =>
              code === this.copyService.getCopy('policy.newVehicleFeatureCodeFrontAirbags') ||
              code === this.copyService.getCopy('policy.newVehicleFeatureCodeSideAirbags') ||
              code === this.copyService.getCopy('policy.newVehicleFeatureCodeFrontAndSideAirbags')
          )
        ) {
          returnArr.push({
            equipmentName: this.copyService.getCopy('policy.newVehicleFeatureAirbagsText'),
            caption: '',
            equipmentOptions: [equipmentOption1, equipmentOption2, equipmentOption3],
            equipmentKey: 'airBagsIndicator'
          });
        }

        if (
          !codesList.find(
            code =>
              code === this.copyService.getCopy('policy.newVehicleFeatureCodeAntiTheftActive') ||
              code === this.copyService.getCopy('policy.newVehicleFeatureCodeAntiTheftPassive')
          ) &&
          isEligibleForAntiTheft
        ) {
          returnArr.push({
            equipmentName: this.copyService.getCopy('policy.newVehicleFeatureAntiTheftDeviceText'),
            caption: this.copyService.getCopy('policy.newVehicleFeatureAntiTheftCaption'),
            equipmentOptions: [equipmentOption1, equipmentOption4, equipmentOption5],
            equipmentKey: 'antiTheftDeviceIndicator'
          });
        }
        return returnArr;
      })
    );
  }

  private buildEquipmentCheckList(): Observable<EquipmentOption[]> {
    return this.vehicleFeaturesCodesList$.pipe(
      take(1),
      map(codesList => {
        const [other1, other2, other3, other4] = this.copyService.getCopy(
          'policy.newVehicleFeatureCheckOptions'
        );
        const returnArr: EquipmentOption[] = [];
        if (
          !codesList.find(
            code =>
              code === this.copyService.getCopy('policy.newVehicleFeatureCodeAntiLockBrakes4w') ||
              code === this.copyService.getCopy('policy.newVehicleFeatureCodeAntiLockBrakesRear')
          )
        ) {
          returnArr.push({ ...other1, optionKey: 'antiLockBreaks' });
        }

        if (
          !codesList.find(
            code =>
              code === this.copyService.getCopy('policy.newVehicleFeatureCodeDaytimeRunningLights')
          )
        ) {
          returnArr.push({ ...other2, optionKey: 'dayTimeRunningLight' });
        }

        if (
          !codesList.find(
            code =>
              code ===
              this.copyService.getCopy('policy.newVehicleFeatureCodeElectronicStabilityControl')
          )
        ) {
          returnArr.push({ ...other3, optionKey: 'electronicStabilityControl' });
        }

        if (returnArr.length > 0) {
          returnArr.push({ ...other4, optionKey: 'needAgent' });
        }

        return returnArr;
      })
    );
  }

  private handleError() {
    this.store
      .select(addVehicleBindQuery.selectVehicleANSNotificationError)
      .pipe(takeUntil(this.stop$))
      .subscribe(agentNotificationError => {
        if (agentNotificationError) {
          this.spinnerService.stop();
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/agent-help']
            })
          );
        } else {
          this.errorEvent.emit({ hasError: false });
        }
      });
  }
}
